import {
  DEFAULT_ANNOTATED_IMAGE_EXPORT_OPTIONS,
  exportAnnotatedImageForEntry,
} from "@/services/annotatedImageExportService";
import { Timestamp } from "firebase/firestore";
import { getAnnotatedThumbnailCacheKey } from "@/utils/cacheKeyUtils";

/**
 * Maximum number of cached thumbnails before evicting oldest entries.
 * 50 covers current plot + adjacent plots with room to spare.
 */
const MAX_CACHE_SIZE = 50;

/**
 * Global LRU cache for pre-rendered annotated thumbnail blob URLs.
 * Keys include updatedAt timestamp to invalidate on re-uploads.
 * Evicts oldest entries and revokes their blob URLs when at capacity.
 */
const annotatedThumbnailCache = new Map<string, string>();

/**
 * Default display options for thumbnail rendering.
 * Shows all annotation elements with text labels.
 * Exported for use in components that render annotated thumbnails.
 */
export const THUMBNAIL_DISPLAY_OPTIONS = {
  ...DEFAULT_ANNOTATED_IMAGE_EXPORT_OPTIONS,
};

/**
 * Get a cached annotated thumbnail URL if available.
 * Refreshes LRU position on access.
 */
export function getCachedAnnotatedThumbnail(
  imageUrl: string,
  metadataPath: string,
  updatedAt?: Timestamp,
  annotationClassNames?: readonly string[],
  annotationClassColorOrder?: readonly string[]
): string | null {
  const key = getAnnotatedThumbnailCacheKey(
    imageUrl,
    metadataPath,
    updatedAt,
    annotationClassNames,
    annotationClassColorOrder
  );
  const url = annotatedThumbnailCache.get(key);
  if (url) {
    // Refresh LRU position by re-inserting
    annotatedThumbnailCache.delete(key);
    annotatedThumbnailCache.set(key, url);
  }
  return url || null;
}

/**
 * Store an annotated thumbnail URL in the cache.
 * Returns the URL that should be used (either cached or newly stored).
 * If already cached, revokes the incoming blobUrl and returns the cached one.
 * Evicts oldest entry if at capacity, revoking its blob URL.
 */
export function setCachedAnnotatedThumbnail(
  imageUrl: string,
  metadataPath: string,
  blobUrl: string,
  updatedAt?: Timestamp,
  annotationClassNames?: readonly string[],
  annotationClassColorOrder?: readonly string[]
): string {
  const key = getAnnotatedThumbnailCacheKey(
    imageUrl,
    metadataPath,
    updatedAt,
    annotationClassNames,
    annotationClassColorOrder
  );

  // If already cached, don't overwrite - prevents revoking URLs that other components may still be displaying
  // (e.g., grid + All Images rendering same thumbnail, or preloader racing with on-demand render)
  // Revoke the incoming blobUrl since it won't be used - prevents memory leak from duplicate renders
  const existingUrl = annotatedThumbnailCache.get(key);
  if (existingUrl) {
    URL.revokeObjectURL(blobUrl);
    // Refresh LRU position
    annotatedThumbnailCache.delete(key);
    annotatedThumbnailCache.set(key, existingUrl);
    return existingUrl;
  }

  // Evict oldest entry if at capacity
  if (annotatedThumbnailCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = annotatedThumbnailCache.keys().next().value;
    if (oldestKey) {
      const oldestUrl = annotatedThumbnailCache.get(oldestKey);
      if (oldestUrl) URL.revokeObjectURL(oldestUrl);
      annotatedThumbnailCache.delete(oldestKey);
    }
  }

  annotatedThumbnailCache.set(key, blobUrl);
  return blobUrl;
}

/**
 * Pre-render an annotated thumbnail and store in cache.
 * Returns the blob URL if successful, null otherwise.
 * If already cached, returns immediately.
 */
export async function preRenderAnnotatedThumbnail(
  imageUrl: string,
  metadataPath: string,
  updatedAt?: Timestamp,
  annotationClassNames?: string[],
  annotationClassColorOrder?: string[]
): Promise<string | null> {
  const key = getAnnotatedThumbnailCacheKey(
    imageUrl,
    metadataPath,
    updatedAt,
    annotationClassNames,
    annotationClassColorOrder
  );

  // Check if already cached
  const cached = annotatedThumbnailCache.get(key);
  if (cached) {
    return cached;
  }

  try {
    const blob = await exportAnnotatedImageForEntry(
      imageUrl,
      metadataPath,
      {
        ...THUMBNAIL_DISPLAY_OPTIONS,
        annotationClassNames,
        annotationClassColorOrder,
      },
      updatedAt
    );
    const blobUrl = URL.createObjectURL(blob);
    // Return the URL from cache - handles race conditions where another call cached first
    return setCachedAnnotatedThumbnail(
      imageUrl,
      metadataPath,
      blobUrl,
      updatedAt,
      annotationClassNames,
      annotationClassColorOrder
    );
  } catch {
    // Silently fail - preloading is best-effort
    return null;
  }
}

/**
 * Check if a thumbnail is cached
 */
export function isAnnotatedThumbnailCached(
  imageUrl: string,
  metadataPath: string,
  updatedAt?: Timestamp,
  annotationClassNames?: readonly string[],
  annotationClassColorOrder?: readonly string[]
): boolean {
  const key = getAnnotatedThumbnailCacheKey(
    imageUrl,
    metadataPath,
    updatedAt,
    annotationClassNames,
    annotationClassColorOrder
  );
  return annotatedThumbnailCache.has(key);
}

/**
 * Invalidate a single cached thumbnail so it is regenerated on next render.
 */
export function invalidateCachedAnnotatedThumbnail(
  imageUrl: string,
  metadataPath: string,
  updatedAt?: Timestamp,
  annotationClassNames?: readonly string[],
  annotationClassColorOrder?: readonly string[]
): void {
  const hasSpecificClassRegistry = Boolean(annotationClassNames?.length);
  const exactKey = getAnnotatedThumbnailCacheKey(
    imageUrl,
    metadataPath,
    updatedAt,
    annotationClassNames,
    annotationClassColorOrder
  );
  const baseKey = getAnnotatedThumbnailCacheKey(imageUrl, metadataPath, updatedAt);
  for (const candidateKey of [...annotatedThumbnailCache.keys()]) {
    const shouldInvalidate = hasSpecificClassRegistry
      ? candidateKey === exactKey
      : candidateKey === baseKey ||
        candidateKey.startsWith(`${baseKey}|classes:`) ||
        candidateKey.startsWith(`${baseKey}|class-colors:`);
    if (!shouldInvalidate) continue;
    const url = annotatedThumbnailCache.get(candidateKey);
    if (url) URL.revokeObjectURL(url);
    annotatedThumbnailCache.delete(candidateKey);
  }
}

/**
 * Clear all cached thumbnails (for memory management if needed)
 */
export function clearAnnotatedThumbnailCache(): void {
  annotatedThumbnailCache.forEach((url) => URL.revokeObjectURL(url));
  annotatedThumbnailCache.clear();
}
